import { useEffect, useState } from "react";

import { Error } from "@material-ui/icons";
import { Button, Input, Modal, Select } from "antd";
import styled from "styled-components";

import { useUser } from "hooks";

import { WorkspaceType } from "models/workspace";

import {
  WORKSPACE_TYPE_OPTIONS,
  workspaceTypeIcon
} from "components/workspace/WorkspaceTypeDisplay";

const DEFAULT_WORKSPACE_TYPE: WorkspaceType = "Personal";

export interface WorkspaceInputModel {
  error: string;
  header: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: { label: string; update: (title: string, type: WorkspaceType) => void };
  visible: boolean;
}

export default function WorkspaceInput({
  error,
  header,
  isLoading,
  onCancel,
  onConfirm,
  visible
}: WorkspaceInputModel) {
  const [workspaceTitle, setWorkspaceTitle] = useState("");
  const [workspaceType, setWorkspaceType] =
    useState<WorkspaceType>(DEFAULT_WORKSPACE_TYPE);

  const { email, isAtLeastPowerUser, user } = useUser();

  // Built from the name parts rather than from useUser's fullName, which interpolates them
  // unguarded — a user missing either name would render the literal "undefined undefined" and
  // never reach the fallback. Joining what exists matches how the workspace service derives
  // ownerName, so this label agrees with the row the user lands on after creating.
  const ownerName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || email || "";

  // A new workspace always starts blank and Personal — never inherits from the
  // workspace the user happens to be viewing.
  useEffect(() => {
    if (!visible) return;

    setWorkspaceTitle("");
    setWorkspaceType(DEFAULT_WORKSPACE_TYPE);
  }, [visible]);

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal
      open={visible}
      onOk={handleCancel}
      onCancel={handleCancel}
      closable={false}
      title={header}
      wrapClassName="copy-dashboard-modal-root"
      footer={[
        <Button key="cancel" onClick={() => onCancel()}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          disabled={isLoading || workspaceTitle.trim() === ""}
          loading={isLoading}
          onClick={() => {
            onConfirm.update(workspaceTitle.trim(), workspaceType);
          }}>
          {onConfirm.label}
        </Button>
      ]}>
      <WorkspaceAddContainer>
        <Options>
          <OptionItem>
            <Label>Title</Label>
            <Input
              autoFocus
              value={workspaceTitle}
              onChange={(e) => setWorkspaceTitle(e.target.value)}
              placeholder="Enter a Workspace's title"
            />
          </OptionItem>

          <OptionItem>
            <Label>Type</Label>
            {isAtLeastPowerUser ? (
              <SelectWrapper
                popupClassName="modal-select"
                value={workspaceType}
                onChange={(value: WorkspaceType) => {
                  setWorkspaceType(value);
                }}
                options={WORKSPACE_TYPE_OPTIONS}
              />
            ) : (
              // Personal is the only type a standard user may create, so this is a read-only
              // field rather than a dropdown offering a single fixed choice — see
              // WorkspaceTypeDisplay for the shared reasoning. Named explicitly because the
              // sibling label is not associated with it, and a disabled input with no
              // accessible name reads as nothing.
              <Input
                aria-label="Type"
                prefix={workspaceTypeIcon(workspaceType)}
                value={workspaceType}
                disabled={true}
              />
            )}
          </OptionItem>

          <OptionItem>
            <Label>Owner</Label>
            <Label>{ownerName}</Label>
          </OptionItem>
        </Options>

        {error !== "" && (
          <ErrorContainer>
            <Error style={{ top: 3 }} /> {error}
          </ErrorContainer>
        )}
      </WorkspaceAddContainer>
    </Modal>
  );
}

const WorkspaceAddContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  .ant-card-body {
    padding: 5px;
    height: 100%;
    flex-direction: row;
    justify-content: space-around;
  }
`;

const OptionItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 5px;
  padding: 2px 0;
`;

const Label = styled.label`
  min-width: 100px;
`;

const SelectWrapper = styled(Select)`
  width: 100%;
`;

const ErrorContainer = styled.div`
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
`;
